import React, { useCallback, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';
import _, { clone } from 'lodash';

import { SwButtonSection, getFetcher } from '../../../../App';
import { Input, PrimaryButton, Sub1Button, Switch } from '@components/atoms';
import { dialogContentsState } from '@components/templates/Dialog';
import SettingCommonMethods from '../../../../utils/settingCommon';
import { useCatchLeave } from '../../../../utils/hooks';

import { SettingWrapper, SettingBox, InputList, SettingList, StyledSend, StyledItemsFlex } from './style';
import { submitDebounce } from '../../../../utils/lib';
import useStaticDialog from 'utils/hooks/useStaticDialog';

export interface DicomInfo {
  action: string;
  dataset: string;
  definition: string;
  dicomTagId?: number;
  method: string;
  tag: string;

  isNumericVR?: boolean;
  isSequential?: boolean;
  value?: string;
  vr: string;
  valid?: boolean;
  isChanged?: boolean;
}
export interface CtpSetting {
  isSendAutomatically: boolean;
  returnCbfColormap: boolean;
  returnCbv: boolean;
  returnColumnedView: boolean;
  returnMismatch: boolean;
  returnMtt: boolean;
  returnTtp: boolean;
  returnSummary: boolean;
  returnTmaxColormap: boolean;
  tags: DicomInfo[];
}

const SendToPacs = () => {
  const [switchIndex, setSwitchIndex] = useState(0);
  const [defaultReportData, setDefaultReportData] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [dicomTagList, setDicomTagList] = useState<DicomInfo[]>([]);

  // result
  const [metaList, setMetaList] = useState<DicomInfo[]>([]); // tags tagCustomizatoinInfos
  const [originMetaList, setOriginMetaList] = useState<DicomInfo[]>([]); // tags isRelayPacsReport

  const [data, setData] = useState<CtpSetting>(); // 처음 받아온 데이터 전체
  const [originValue, setOriginValue] = useState({
    mismatch: true,
    tmax: false,
    CBF: false,
    CBV: false,
    MTT: false,
    TTP: false,
    summary: true,
    ColumnedView: true,
    isSendAutomatically: false,
    analyticalInformation: true,
  });
  const [value, setValue] = useState({
    mismatch: true,
    tmax: false,
    CBF: false,
    CBV: false,
    MTT: false,
    TTP: false,
    summary: true,
    ColumnedView: true,
    isSendAutomatically: false,
    analyticalInformation: true,
  });
  const { mismatch, tmax, CBF, CBV, MTT, TTP, summary, ColumnedView, analyticalInformation } = value;

  const setDialogContents = useSetRecoilState(dialogContentsState);
  const { metaListLocalValid, metaListChangeData, getDicomTagAll, errorHandler, checkValidAllClear } =
    SettingCommonMethods();

  const { confirmTurnOnOffDialog } = useStaticDialog();

  // 값 변경이 있는 경우 새로고침, 탭닫기 막기
  useCatchLeave(isChanged);

  /**
   * dicom tag 가져오기 전체다이콤태그
   */
  const getDicomTag = async () => {
    const _result = await getDicomTagAll();
    setDicomTagList(_result.result);
  };

  /**
   * setting 데이터 가져오기
   */
  const getSettingData = async () => {
    const url = 'api/setting/ctp/pacs'; // ok
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          const _result = {
            mismatch: r.result.returnMismatch,
            tmax: r.result.returnTmaxColormap,
            CBF: r.result.returnCbfColormap,
            CBV: r.result.returnCbv,
            MTT: r.result.returnMtt,
            TTP: r.result.returnTtp,
            ColumnedView: r.result.returnColumnedView,
            summary: r.result.returnSummary,
            isSendAutomatically: r.result.isSendAutomatically,
            analyticalInformation: r.result.returnAnalyticalInformation,
          };
          setData(r.result);
          setValue(_result);
          setOriginValue(_result);
          r.result.tags.forEach((element: DicomInfo) => {
            element.valid = true;
          });
          setOriginMetaList(r.result.tags);
          setMetaList(r.result.tags);
          setDefaultReportData(r.result.isSendAutomatically);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * 체크박스 토글
   */
  const changeSetting = (e: any) => {
    const { name, checked } = e.target;
    let obj: { [key: string]: boolean } = { [name]: checked };
    setValue((prev) => ({ ...prev, ...obj }));
  };

  /**
   * 변경값 저장 하기
   */
  const postReportSetting = async () => {
    // valid 체크
    const url = `api/setting/ctp/pacs`;
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;
    const _tagList: any = [];

    // ctp setting 데이터 저장
    // if (reportData) {
    metaList.forEach((data) => {
      const _tmp = {
        dicomTagId: data.dicomTagId,
        isSequential: data.isSequential,
        value: data.value,
      };
      _tagList.push(_tmp);
    });
    // _body.tags = _tagList;
    // }

    const _body = {
      isSendAutomatically: value.isSendAutomatically,
      returnCbfColormap: value.CBF,
      returnCbv: value.CBV,
      returnColumnedView: value.ColumnedView,
      returnMismatch: value.mismatch,
      returnMtt: value.MTT,
      returnTtp: value.TTP,
      returnSummary: value.summary,
      returnTmaxColormap: value.tmax,
      returnAnalyticalInformation: value.analyticalInformation,
      tags: _tagList,
    };

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: _body,
    })(url)
      .then(async (r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: `It has been reflected.`,
            footer: (
              <PrimaryButton
                onClick={async () => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setDefaultReportData(value.isSendAutomatically);
          setOriginMetaList(metaList);
          setOriginValue(value);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * report 사용여부 변경 전 체크
   */
  const isChangeSetting = async (e: boolean) => {
    setValue((prev) => ({ ...prev, ...{ isSendAutomatically: e } }));

    const isOn = e;

    setDialogContents({
      type: 'plain',
      body: `Are you sure you want to turn ${
        isOn ? 'on' : 'off'
      } automatically image send? \n\r(You must click Apply for the changes to take effect.)`,
      footer: (
        <>
          <Sub1Button
            onClick={() => {
              setValue((prev) => ({ ...prev, ...{ isSendAutomatically: !e } }));
              setDialogContents({ type: 'hidden' });
            }}
          >
            {`No, I don't`}
          </Sub1Button>

          <PrimaryButton
            onClick={async () => {
              setMetaList(originMetaList);
              setDialogContents({ type: 'hidden' });
              setValue((prev) => ({ ...prev, ...{ isSendAutomatically: e } }));
            }}
          >
            Yes, turn {isOn ? 'on' : 'off'}
          </PrimaryButton>
        </>
      ),
    });
  };

  /**
   * metaList 변경 또는 유효성 확인
   * @param e
   * @param index
   * @param type
   */
  const metaListChange = useCallback(
    (e: any, index: number, type: string) => {
      metaListChangeData(e, index, type, metaList, originMetaList, setMetaList);
    },
    [metaList, originMetaList],
  );

  /**
   * 최초 데이터 설정
   */
  useEffect(() => {
    (async () => {
      await getSettingData();
      await getDicomTag();
    })();
  }, []);

  useEffect(() => {
    // 값이 바꼈을 때 true
    // 유효성검사 통과 되었을 때 true
    const successList = metaList.filter((item, i) => {
      item.valid;
    });
    // const isSameMetalistValid = successList.length === metaList.length;
    const isSameMetalistValid = !checkValidAllClear(metaList, originMetaList);

    const isSameValue = _.isEqual(value, originValue);
    const isSameMetalist = _.isEqual(metaList, originMetaList);
    // if(){
    setIsChanged(isSameMetalistValid && isSameValue);
    // }
  }, [value, originValue, metaList]);

  return (
    <>
      <label>Send to PACS</label>

      {/* {value.isSendAutomatically && ( */}
      <>
        <SettingWrapper>
          <strong>Image</strong>

          <SettingBox>
            <StyledSend>
              <span className={'send-title'}>Send automatically</span>
              <span className={'send-off'}>Off</span>
              <Switch
                key={switchIndex}
                defaultValue={value.isSendAutomatically}
                onChangeValue={(e) => isChangeSetting(e)}
              />
              <span className={'send-on'}>On</span>
            </StyledSend>
            {value.isSendAutomatically && (
              <StyledItemsFlex>
                <span className={'send-title'}>Send items</span>
                <div className={'send-content'}>
                  <SettingList>
                    <li>
                      <InputList>
                        <li>
                          <Input
                            type={'checkbox'}
                            name={'summary'}
                            disabled={!value.isSendAutomatically}
                            checked={summary}
                            onChange={(e) => changeSetting(e)}
                          >
                            Summary (Mismatch, Tmax, CBF, AIF/VOF)
                          </Input>
                        </li>
                      </InputList>
                    </li>
                  </SettingList>
                  <SettingList>
                    <li>
                      <InputList>
                        <li>
                          <Input
                            type={'checkbox'}
                            name={'mismatch'}
                            disabled={!value.isSendAutomatically}
                            checked={mismatch}
                            onChange={(e) => changeSetting(e)}
                          >
                            Mismatch
                          </Input>
                        </li>
                        <li>
                          <Input
                            type={'checkbox'}
                            name={'tmax'}
                            disabled={!value.isSendAutomatically}
                            checked={tmax}
                            onChange={(e) => changeSetting(e)}
                          >
                            Tmax(color map)
                          </Input>
                        </li>

                        <li>
                          <Input
                            type={'checkbox'}
                            name={'CBF'}
                            disabled={!value.isSendAutomatically}
                            checked={CBF}
                            onChange={(e) => changeSetting(e)}
                          >
                            CBF(color map)
                          </Input>
                        </li>
                        <li>
                          <Input
                            type={'checkbox'}
                            name={'CBV'}
                            disabled={!value.isSendAutomatically}
                            checked={CBV}
                            onChange={(e) => changeSetting(e)}
                          >
                            CBV
                          </Input>
                        </li>
                      </InputList>
                    </li>
                  </SettingList>
                  <SettingList>
                    <li>
                      <InputList>
                        <li>
                          <Input
                            type={'checkbox'}
                            name={'MTT'}
                            disabled={!value.isSendAutomatically}
                            checked={MTT}
                            onChange={(e) => changeSetting(e)}
                          >
                            MTT
                          </Input>
                        </li>
                        <li>
                          <Input
                            type={'checkbox'}
                            name={'TTP'}
                            disabled={!value.isSendAutomatically}
                            checked={TTP}
                            onChange={(e) => changeSetting(e)}
                          >
                            TTP
                          </Input>
                        </li>
                        <li>
                          <Input
                            type={'checkbox'}
                            name={'ColumnedView'}
                            disabled={!value.isSendAutomatically}
                            checked={ColumnedView}
                            onChange={(e) => changeSetting(e)}
                          >
                            Columned view
                          </Input>
                        </li>
                        <li>
                          <Input
                            type={'checkbox'}
                            name={'analyticalInformation'}
                            disabled={!value.isSendAutomatically}
                            checked={analyticalInformation}
                            onChange={(e) => changeSetting(e)}
                          >
                            Analytical Information
                          </Input>
                        </li>
                      </InputList>
                    </li>
                  </SettingList>
                </div>
              </StyledItemsFlex>
            )}
          </SettingBox>
        </SettingWrapper>
      </>
      {/* )} */}

      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            margin-top: 8px;
            font-size: 18px;
          `}
          onClick={submitDebounce(postReportSetting)}
          disabled={isChanged}
        >
          Apply
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default SendToPacs;
